import React, { useState } from 'react';
import { App, Button, Empty, Image, Popconfirm, Space, Spin, Tooltip, Typography } from 'antd';
import { DeleteOutlined, DownloadOutlined, FilePdfOutlined, WarningOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { canManageFinanzas, isSupportUser } from '../../utils/roleHelpers';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import {
  useGetExpenseAttachmentsQuery,
  useGetExpenseAttachmentContentQuery,
  useDeleteExpenseAttachmentMutation,
} from '../../services/expensesApi';
import { ExpenseAttachmentDto } from '../../types/expense';

const { Text } = Typography;

interface Props {
  restaurantId: string;
  expenseId: string;
  /** Permite ocultar el borrado incluso para admin (p.ej. gastos cancelados). */
  allowDelete?: boolean;
}

/**
 * Miniatura de un comprobante.
 *
 * El JWT viaja en header (baseApi), así que un `<img src="/api/.../content">` no
 * funciona: el browser no manda el Authorization header al cargar una imagen. Hay
 * que traer los bytes y usar un object URL — y eso va por el endpoint RTK, no por
 * un fetch a mano, para heredar el refresh de token y el manejo de SESSION_EXPIRED
 * de baseQueryWithReauth.
 */
const AttachmentThumb: React.FC<{
  restaurantId: string;
  attachment: ExpenseAttachmentDto;
  onDelete?: () => void;
  canDelete: boolean;
  deleting: boolean;
}> = ({ restaurantId, attachment, onDelete, canDelete, deleting }) => {
  const { formatInTimeZone } = useRestaurantTimeZone();
  const isPdf = attachment.mimeType === 'application/pdf';

  const { data: objectUrl, isFetching, isError } = useGetExpenseAttachmentContentQuery({
    restaurantId,
    expenseId: attachment.expenseId,
    attachmentId: attachment.id,
  });

  const footer = (
    <div style={{ marginTop: 4, lineHeight: 1.3 }}>
      <Text style={{ fontSize: 11 }} ellipsis={{ tooltip: attachment.fileName }}>
        {attachment.fileName}
      </Text>
      <div>
        <Text type="secondary" style={{ fontSize: 10 }}>
          {/* Quién subió qué y cuándo es justamente lo que el dueño quiere ver. */}
          {attachment.uploadedByUserName || 'Usuario'} ·{' '}
          {formatInTimeZone(attachment.uploadedAt, 'dd MMM HH:mm')}
        </Text>
      </div>
    </div>
  );

  // El object URL sirve igual para <img src> y para descargar, así que los PDF
  // también se traen: son ≤5 MB y hay ≤5 por gasto. Se muestran con icono porque
  // no se pueden previsualizar en un <img>, pero se descargan igual.
  const download = () => {
    if (!objectUrl) return;
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = attachment.fileName;
    a.click();
  };

  return (
    <div style={{ width: 120 }}>
      <div
        style={{
          width: 120,
          height: 120,
          // Variables del tema: los hex fijos no voltean en modo oscuro.
          border: '1px solid var(--border-color)',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          background: 'var(--bg-secondary)',
        }}
      >
        {isPdf ? (
          <FilePdfOutlined style={{ fontSize: 40, color: 'var(--color-error-text)' }} />
        ) : isFetching ? (
          <Spin size="small" />
        ) : isError || !objectUrl ? (
          // Estado de error visible por miniatura, no un fallo mudo: si el
          // comprobante devuelve 403/404 el usuario tiene que enterarse.
          <Tooltip title="No se pudo cargar este comprobante">
            <WarningOutlined style={{ fontSize: 28, color: 'var(--color-warning)' }} />
          </Tooltip>
        ) : (
          <Image
            src={objectUrl}
            alt={attachment.fileName}
            width={120}
            height={120}
            style={{ objectFit: 'cover' }}
          />
        )}
      </div>

      {footer}

      <Space size={4} style={{ marginTop: 2 }}>
        {objectUrl && (
          <Tooltip title="Descargar">
            <Button type="text" size="small" icon={<DownloadOutlined />} onClick={download} />
          </Tooltip>
        )}
        {canDelete && onDelete && (
          <Popconfirm
            title="¿Eliminar este comprobante?"
            description="Queda registrado quién lo eliminó y cuándo."
            okText="Eliminar"
            okButtonProps={{ danger: true }}
            cancelText="Cancelar"
            onConfirm={onDelete}
          >
            <Tooltip title="Eliminar">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} loading={deleting} />
            </Tooltip>
          </Popconfirm>
        )}
      </Space>
    </div>
  );
};

/**
 * Galería de comprobantes de un gasto.
 *
 * El botón de eliminar sólo se renderiza para quien puede gestionar Finanzas — un
 * Gerente los ve pero no los quita. La UI oculta el botón; el gate real es el
 * [Authorize] del endpoint DELETE.
 */
const ExpenseAttachmentsViewer: React.FC<Props> = ({ restaurantId, expenseId, allowDelete = true }) => {
  const { message } = App.useApp();
  const user = useSelector((state: RootState) => state.auth?.user);
  const canDelete = allowDelete && canManageFinanzas(user?.role, isSupportUser(user?.email));

  const { data: attachments, isLoading } = useGetExpenseAttachmentsQuery({ restaurantId, expenseId });
  const [deleteAttachment] = useDeleteExpenseAttachmentMutation();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (attachmentId: string) => {
    setDeletingId(attachmentId);
    try {
      await deleteAttachment({ restaurantId, expenseId, attachmentId }).unwrap();
      message.success('Comprobante eliminado');
    } catch (err: any) {
      message.error(err?.data?.message || 'No se pudo eliminar el comprobante.');
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) return <Spin size="small" />;

  if (!attachments || attachments.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={<Text type="secondary">Sin comprobantes</Text>}
        style={{ margin: '8px 0' }}
      />
    );
  }

  return (
    <Image.PreviewGroup>
      <Space wrap size={12} align="start">
        {attachments.map((a) => (
          <AttachmentThumb
            key={a.id}
            restaurantId={restaurantId}
            attachment={a}
            canDelete={canDelete}
            deleting={deletingId === a.id}
            onDelete={() => handleDelete(a.id)}
          />
        ))}
      </Space>
    </Image.PreviewGroup>
  );
};

export default ExpenseAttachmentsViewer;
